import Vue from 'vue'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import { applicationManageMixin, archivableMixin, statusesMixin, waitForRefreshApplicationMixin } from './mixins'
import MobileActionsCard from './MobileActionsCard/MobileActionsCard.vue'
import TalentDetailsCard from './TalentDetailsCard/TalentDetailsCard.vue'
import VacancyDetailsCard from './VacancyDetailsCard/VacancyDetailsCard.vue'
import { getWorker } from '@/api/usersAndProfiles/profiles'
import { getVacancy } from '@/api/vacancies/vacancies'
import { CHAT, VACANCY_DETAILS, VACANCY_DETAILS_ADMIN_APPLICATIONS } from '@/constants/routes'
import { AccountTypes } from '@/constants/user/accountTypes'
import responseMixin from '@/mixins/responseMixin'
import rolebleMixin from '@/mixins/rolebleMixin'
import snackMixin from '@/mixins/snackMixin'
import unreadMessagesBlinkTitleMixin from '@/mixins/unreadMessagesBlinkTitleMixin'
import ChatParticipant from '@/models-ts/chat/ChatParticipant'
import FreelancerDetails from '@/models-ts/user/details/FreelancerDetails'
import UserInfo from '@/models-ts/user/UserInfo'
import Vacancy from '@/models-ts/vacancies/Vacancy'
import Chat from '@/partials/ChatNew/Chat.vue'
import ChatRoomPrimaryInfo from '@/partials/ChatRoomPrimaryInfo/ChatRoomPrimaryInfo.vue'
import { RootState } from '@/store'

export default Vue.extend<any, any, any, any>({
  mixins: [
    applicationManageMixin,
    archivableMixin,
    responseMixin,
    rolebleMixin,
    snackMixin,
    statusesMixin,
    unreadMessagesBlinkTitleMixin,
    waitForRefreshApplicationMixin,
  ],
  components: {
    MobileActionsCard,
    Chat,
    VacancyDetailsCard,
    TalentDetailsCard,
    ChatRoomPrimaryInfo,
  },
  data() {
    return {
      initLoaded: false,
      withDetails: false,
      vacancy: null,
      freelancer: null,
      lastUpdatedRoomDate: null,
    }
  },
  computed: {
    ...mapState<RootState>({
      userIsLoaded: (state: RootState) => state.user.profile.isLoaded,
      userId: (state: RootState) => state.user?.id,
      roomId: (state: RootState) => state.chatNew.openedRoomId,
      initialized: (state: RootState) => state.chatNew.initialized,
      application: (state: RootState) => state.vacancyChat.application.value,
    }),
    ...mapGetters({
      room: 'chatNew/getOpenedRoom',
    }),
    isFreelancerInfo() {
      return this.isTemporary || (this.application && !this.isFreelancer)
    },
    hasActions() {
      return !this.isTemporary && this.application
    },
    vacancyStage() {
      return this.application?.vacancy?.stage || this.vacancy?.stage
    },
    roomUser() {
      if (this.application) {
        return this.isFreelancer ? this.application.customer : this.application.freelancer
      }
      return new UserInfo({
        id: this.freelancer.id,
        name: this.freelancer.fullName || '',
        type: AccountTypes.USER_ACCOUNT_SIMPLE,
        avatar: this.freelancer.avatar,
        avgReviews: this.freelancer.avgReview,
        reviewsCount: this.freelancer.reviews.length,
        is_customer_verified: false,
      })
    },
    roomEntity() {
      if (!this.initLoaded) return
      if (this.application) {
        return {
          name: this.application.vacancy.name,
          link: {
            name: this.isFreelancer ? VACANCY_DETAILS : VACANCY_DETAILS_ADMIN_APPLICATIONS,
            params: {
              id: this.application.vacancy.id,
              slug: this.application.vacancy.slug,
            },
          },
        }
      }
      return {
        name: this.vacancy?.name,
        link: {
          name: VACANCY_DETAILS_ADMIN_APPLICATIONS,
          params: {
            id: this.vacancy.id,
            slug: this.vacancy.slug,
          },
        },
      }
    },
    backLink() {
      return this.roomEntity?.link
    },
    applicationIsArchived() {
      return this.isFreelancer && this.application.meta?.archived
    },
    isMessaged() {
      return this.application.meta?.messaged
    },
  },
  watch: {
    initialized: {
      handler() {
        if (this.initialized) {
          if (!this.roomId) {
            const room = this.$route.query.room
            if (room) {
              this.openRoom(room)
            } else {
              this.$router.replace({ name: CHAT })
            }
          }
        }
      },
      immediate: true,
    },
    roomId: {
      async handler() {
        if (this.roomId) {
          this.initLoaded = false
          if (!this.isTemporary) {
            await this.getApplication(this.applicationId)
            this.initLoaded = true
          } else if (this.room) {
            this.vacancy = this.$store.getters['vacancyDetails/vacancy']
            if (!this.vacancy) {
              const vacancy = await getVacancy(this.room.entity.id)
              this.vacancy = Vacancy.fromServer(vacancy)
            }
            const freelancerId = this.room.participants.find((user: ChatParticipant) => String(user.userId) !== String(this.userId))?.userId
            if (freelancerId) {
              this.freelancer = FreelancerDetails.fromServer(await getWorker(freelancerId))
            }
            this.initLoaded = true
          } else {
            this.$router.replace({ name: CHAT })
          }
        }
        if (this.room && this.roomId !== this.$route.query.room) {
          this.$router.replace(this.room.chatLink)
        }
      },
      immediate: true,
    },
    'room.updatedAt': {
      handler() {
        if (this.lastUpdatedRoomDate) {
          let dateA = Number(new Date(this.lastUpdatedRoomDate))
          let dateB = Number(new Date(this.room.updatedAt))
          if (Math.abs(dateA - dateB) > 1000) {
            this.waitRefreshApplication()
          }
        }
        this.lastUpdatedRoomDate = this.room?.updatedAt
      },
      immediate: true,
    },
  },
  beforeDestroy() {
    this.closeRoom()
  },
  methods: {
    ...mapMutations({
      updateApplication: 'vacancyChat/updateApplication',
    }),
    ...mapActions({
      openRoom: 'chatNew/openRoom',
      closeRoom: 'chatNew/closeRoom',
      getApplication: 'vacancyChat/getApplication',
      setApplicationMeta: 'vacancyDetails/setApplicationMeta',
    }),
    onDeclineApplicationByCustomerClick() {
      this.declineApplicationByCustomer()
    },
    onCancelApplicationByFreelancerClick() {
      this.declineApplicationByFreelancer()
    },
    async onRemoveFromArchivedClick() {
      if (!this.archiving) {
        await this.setArchivedMeta(false)
        this.openSnackbar({
          text: 'Application was unarchived',
        })
      }
    },
    async onMessageSent() {
      const meta = { ...this.application.meta }
      let needUpdate = false
      if (this.isMetaArchived) {
        meta.archived = false
        needUpdate = true
      }
      if (!this.isMessaged) {
        meta.messaged = true
        needUpdate = true
      }

      if (needUpdate) {
        await this.setApplicationMeta({
          vacancyId: this.application.vacancyId,
          applicationId: this.application.id,
          meta,
        })
        this.updateApplication({ id: this.application.id, meta })
      }
    },
  },
  metaInfo() {
    return {
      title: this.getMetaTitle(this.application?.vacancy?.name || 'Chat'),
    }
  },
})
